import { addNutrients, calculateAverageContrib, calculateAverageNutrients, getAppNutrients, getRequirementsLeft, getRequirementsNutrients } from "../../nutrients"
import { getDistinctValues, labels, selectMany } from "../../omnia"
import { BalanceProvider } from "../../services/balance"
import { IFieldsService, INtrApp, INtrNutrients, INtrReq, INutritionPlan, INutritionPlanProductApp, INutritionService, IRequirementsProvider } from "../../types"
import { sortPlansByCalcOrder } from "../../utils"
import { AppVm } from "./app"
import { PlanVm } from "./plan"
import { ICroppingApplication, ICroppingPlan, IPlanCropping } from "./types"

export class PlanReader {

    private requirements: IRequirementsProvider;
    private applications: INtrApp[];
    private _balance: BalanceProvider

    private sortedPlans: INutritionPlan[]

    private plans: PlanVm[];


    private croppingPlans: { [key: number]: ICroppingPlan[] };

    constructor(private nutritionSvc: INutritionService, private fieldSvc: IFieldsService) {
        this.refresh();
    }

    public refresh() {

        this.requirements = this.nutritionSvc.getRequirementsProvider();
        this.applications = this.nutritionSvc.getApplications(false);
        this._balance = this.nutritionSvc.balance

        const plans = this.nutritionSvc.getPlans(false)
        this.sortedPlans = sortPlansByCalcOrder(plans, p => p)

        this.croppingPlans = {};

        this.plans = this.setupPlans();

    }

    get balance() {
        return this._balance
    }

    getCroppingInitialRequirements(croppingId: number): INtrReq {
        return this.requirements.getCroppingInitialRequirements(croppingId);
    }

    getInitialRequirements(croppingId: number, zones?: boolean): INtrReq[] {
        return this.requirements.getInitialRequirements(croppingId, zones);
    }

    getClosingRequirements(croppingId: number, zones?: boolean): INtrReq[] {
        return this.requirements.getClosingRequirements(croppingId, zones);
    }

    getApplications(croppingId: number, planId: number, appId: number) {
        return this.applications.filter(a => (a.CroppingId == croppingId) && (a.PlanId == planId) && (a.AppId == appId) && (!a.Aggregate));
    }

    getPlanRequirements(croppingId: number, planId: number) {
        return this.requirements.getPlanRequirements(croppingId, planId).filter(r => !r.Aggregate);
    }

    getPlans(): PlanVm[] {
        return this.plans.slice();
    }

    getPlan(planId: number): PlanVm {
        return this.plans.find(p => p.planId == planId);
    }

    private setupPlans(): PlanVm[] {

        const plans = this.sortedPlans

        let croppingIds = getDistinctValues(selectMany(plans.filter(p => p.Cropping).map(p => p.Cropping.map(c => c.CroppingId))), id => id);

        let croppingPlans = selectMany(croppingIds.map(id => this.getCroppingPlans(id)));

        let vm = plans.map(p => this.setupPlan(p, croppingPlans));

        return vm;

    }

    getCroppingPlans(croppingId: number): ICroppingPlan[] {

        let plans = this.croppingPlans[croppingId];

        if (!plans) {
            plans = this.setupCroppingPlans(croppingId);
            this.croppingPlans[croppingId] = plans;
        }

        return plans;

    }

    getCroppingPlan(croppingId: number, planId: number): ICroppingPlan {
        let plans = this.getCroppingPlans(croppingId);
        if (plans) {
            return plans.find(p => p.planId == planId);
        }
    }

    private setupCroppingPlans(croppingId: number): ICroppingPlan[] {

        const plans = this.sortedPlans.filter(p => !!p.Cropping.find(c => c.CroppingId == croppingId));

        let models = plans.map(p => {

            let requirements = this.requirements.getPlanRequirements(croppingId, p.NutritionPlanId).find(r => !r.Zone);
            let nutrients = getRequirementsNutrients(requirements);

            let planInfo = this.setupCroppingPlan(croppingId, p, nutrients);

            return planInfo;

        }).filter(p => !!p);

        return models;

    }


    private setupPlan(p: INutritionPlan, croppingPlans: ICroppingPlan[]): PlanVm {

        let planCropping = (p.Cropping || []).map(c => {

            let croppingPlan = croppingPlans.find(cp => (cp.croppingId == c.CroppingId) && (cp.planId == p.NutritionPlanId));

            if (croppingPlan) {

                let applications = (croppingPlan.applications || []).map(a => new AppVm(croppingPlan, a));

                let cropping = this.fieldSvc.findCropping(c.CroppingId);

                let croppingInfo = <IPlanCropping>{
                    plan: p,
                    croppingId: c.CroppingId,
                    cropping: cropping,
                    area: cropping ? cropping.area : null,
                    applications: applications,
                    openingReq: croppingPlan.openingReq,
                    contrib: croppingPlan.contrib,
                    closingReq: croppingPlan.closingReq,
                };

                return croppingInfo;
            }

        }).filter(i => !!i);

        let planVm = new PlanVm(p, planCropping);

        planVm.openingReq = calculateAverageNutrients(planCropping.map(c => {
            return {
                area: c.cropping.area,
                nutrients: c.openingReq
            }
        }));

        planVm.contrib = calculateAverageContrib(planCropping.map(c => {
            return {
                area: c.cropping.area,
                contrib: c.contrib
            }
        }));

        if (planVm.openingReq) {
            planVm.closingReq = getRequirementsLeft(planVm.openingReq, planVm.contrib);
        }

        return planVm;

    }

    private setupCroppingPlan(croppingId: number, plan: INutritionPlan, requirements: INtrNutrients): ICroppingPlan {

        let applications = plan.Applications;

        applications = applications.sort((a1, a2) => a1.ApplicationDate.valueOf() - a2.ApplicationDate.valueOf());

        let manure = applications.some(a => !!a.Manure);

        let cropping = this.fieldSvc.findCropping(croppingId);

        let planInfo = <ICroppingPlan>{
            croppingId: croppingId,
            cropping: cropping,
            area: cropping ? cropping.area : null,
            plan: plan,
            planId: plan.NutritionPlanId,
            name: plan.Name,
            precision: plan.Precision,
            manure: manure,
            applications: null,
            openingReq: requirements
        };

        planInfo.applications = applications.map(a => {

            let results = this.getCroppingApplication(croppingId, planInfo, a, requirements);

            if (results) {
                return results.appInfo;
            }

        }).filter(a => !!a);

        planInfo.closingReq = requirements;
        planInfo.contrib = addNutrients(planInfo.applications.map(a => a.contrib));

        return planInfo;

    }

    private getCroppingApplication(croppingId: number, plan: ICroppingPlan, app: INutritionPlanProductApp, requirements: INtrNutrients): { appInfo: ICroppingApplication, requirements: INtrNutrients } {

        let appDetails = this.applications.find(a =>
            (a.CroppingId == croppingId)
            && (a.AppId == app.NutritionPlanProductAppId)
            && (!a.Zone)
        );

        if (!appDetails) {
            return;
        }

        let productRate = appDetails.ProductRate;

        let units = labels.FertiliserAppUnit(app.ProductUnit);
        if (units) units += "/ha";

        let zones = this.getApplicationZones(croppingId, plan.planId, appDetails.AppId);

        let contrib = getAppNutrients(appDetails);
        requirements = getRequirementsLeft(requirements, contrib);

        let appInfo = <ICroppingApplication>{

            plan: plan,

            app: app,
            appId: app.NutritionPlanProductAppId,

            croppingId: croppingId,
            area: plan.area,

            targetNutrient: app.TargetNutrient,
            date: app.ApplicationDate,

            stockName: app.Manure ? app.Manure.Name : app.Fertiliser ? app.Fertiliser.Name : null,

            productRate: productRate,
            units: units,

            zones: zones,

            contrib: contrib,
            closingReq: requirements,

        };

        return {
            appInfo: appInfo,
            requirements: requirements,
        };

    }

    private getApplicationZones(croppingId: number, planId: number, appId: number): INtrApp[] {

        let apps = this.applications.filter(a => (a.CroppingId == croppingId) && (a.PlanId == planId) && (a.AppId == appId) && (!a.Aggregate));

        return apps;

    }

}
